import axios from 'axios';
import Cookies from 'js-cookie';
import { reportApiError, reportApiRejection } from '../utils/report-api-error';
import { diagnosticTags } from '../utils/tracker-diagnostics';
import { sessionCookie, sessionId, newSession, getAccessToken, getRefreshToken, storeTokens, removeTokens, revokeSession, retireSession, storeProfile } from './auth-storage';
export { getAccessToken, getRefreshToken } from './auth-storage';

const baseURL = process.env.REACT_APP_BACKEND_BASE_URL || '';
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '6024',
  },
});

let generation = 0;
let refreshFlight;
let pendingLogout;

// Capture this before asynchronous work, and check again before caller side effects.
export const authGuard = () => {
  const started = generation;
  const session = sessionId();
  return () => generation === started && sessionId() === session;
};
const changedAuth = () => new axios.CanceledError('Authentication changed');
export const isAuthCancelled = error => axios.isCancel(error);

export const clearAuth = (previous = sessionId()) => {
  const clearedGeneration = ++generation;
  const clearedSession = previous && !previous.startsWith('legacy:')
    ? (previous.startsWith('revoked:') ? previous : `revoked:${previous}`) : undefined;
  // Revoke only the captured session. Never overwrite the active pointer: a
  // different tab may install a new login during this synchronous cleanup.
  revokeSession(previous);
  refreshFlight = undefined;
  pendingLogout = undefined;
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
  ['userId', 'userName', 'userEmail', 'loginUser'].forEach(name => Cookies.remove(name));
  return () => generation === clearedGeneration && sessionId() === clearedSession;
};

export const setAuth = (tokens, profile = {}) => {
  const previous = sessionId();
  clearAuth(previous);
  const session = newSession();
  storeTokens(tokens, session);
  storeProfile(profile, session);
  Cookies.set(sessionCookie, session, { expires: 365, sameSite: 'Lax' });
  // Inactive namespaces cannot become active again; avoid accumulating cookies.
  retireSession(previous);
};

export const isAuthenticated = () => !!((pendingLogout?.isCurrent() && pendingLogout) || getAccessToken() || getRefreshToken());

export async function logout() {
  // Preserve the captured credential in memory on failure so the same button can
  // retry revocation even though local requests and refresh have been invalidated.
  if (pendingLogout && !pendingLogout.isCurrent()) pendingLogout = undefined;
  if (!pendingLogout) {
    const session = sessionId();
    const accessToken = getAccessToken(session);
    const refreshToken = getRefreshToken(session);
    const isCurrent = clearAuth(session);
    pendingLogout = { accessToken, refreshToken, isCurrent };
  }
  const attempt = pendingLogout;
  try {
    await axios.post(`${baseURL}/api/user/logout`, { refreshToken: attempt.refreshToken }, {
      skipSentryReporting: true,
      headers: { ...(attempt.accessToken ? { Authorization: `Bearer ${attempt.accessToken}` } : {}),
        'ngrok-skip-browser-warning': '6024' },
    });
    if (attempt.isCurrent()) pendingLogout = undefined;
    return { isCurrent: attempt.isCurrent };
  } catch (error) {
    if (!attempt.isCurrent()) throw changedAuth();
    // A definitive rejection means this session is already invalid.
    if (error.response?.status === 401) {
      pendingLogout = undefined;
      return { isCurrent: attempt.isCurrent };
    }
    reportApiError(error);
    throw error;
  }
}

// Bind identity at invocation, before a concurrent login can install new tokens.
api.interceptors.request.use(config => {
  // Explicit public login may supersede logout; protected background work cannot.
  if (pendingLogout && !pendingLogout.isCurrent()) pendingLogout = undefined;
  if (pendingLogout && !config.skipAuthRefresh) throw changedAuth();
  if (config._authGeneration !== undefined && config._authGeneration !== generation) throw changedAuth();
  const session = sessionId();
  if ('_authSession' in config && config._authSession !== session) throw changedAuth();
  config._authGeneration = generation;
  config._authSession = session;
  config.headers['X-Roam-Diagnostic-ID'] = diagnosticTags().diagnostic_id;
  const accessToken = getAccessToken(config._authSession);
  if (sessionId() !== config._authSession) throw changedAuth();
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  else delete config.headers.Authorization;
  return config;
}, error => {
  // Axios calls this directly for synchronous interceptors; stop dispatch and
  // preserve the CanceledError that callers use to suppress stale side effects.
  throw error;
}, { synchronous: true });

function unauthorized(session) {
  const isCurrent = clearAuth(session);
  if (isCurrent()) window.dispatchEvent(new CustomEvent('api:unauthorized'));
  else throw changedAuth();
}

api.interceptors.response.use(response => {
  if (response.config._authGeneration !== generation || response.config._authSession !== sessionId()) throw changedAuth();
  return response;
}, async error => {
  const original = error.config;
  if (!original) throw error;
  if (original._authGeneration !== generation || original._authSession !== sessionId()) throw changedAuth();
  if (error.response?.status !== 401 || original._retry || original.skipAuthRefresh) throw error;
  // Mark every waiter before it joins the shared flight, not just its initiator.
  original._retry = true;
  const isCurrent = () => original._authGeneration === generation && original._authSession === sessionId();
  const currentAccess = getAccessToken(original._authSession);
  // A slower 401 may arrive after the shared refresh has already completed.
  if (currentAccess && original.headers.Authorization !== `Bearer ${currentAccess}`) return api(original);
  const refreshToken = getRefreshToken(original._authSession);
  if (!refreshToken) {
    unauthorized(original._authSession);
    throw error;
  }
  if (!refreshFlight) {
    const refresh = async () => {
      if (!isCurrent()) throw changedAuth();
      // Another tab may have refreshed while this tab waited for the lock.
      if (getAccessToken() !== currentAccess || getRefreshToken() !== refreshToken) return;
      return axios.post(`${baseURL}/api/user/refresh-token`, { refreshToken }, { skipSentryReporting: true })
      .then(response => {
        if (!isCurrent()) throw changedAuth();
        storeTokens(response.data, original._authSession);
        // A different tab can change identity during the cookie writes. Those
        // writes target only the old session; discard them and never retry.
        if (!isCurrent()) {
          removeTokens(original._authSession);
          throw changedAuth();
        }
      }).catch(refreshError => {
        if (!isCurrent()) throw changedAuth();
        // Only definitive invalid-refresh responses end the local session.
        // Transport, forbidden and server failures remain explicitly retryable.
        if ([400, 401, 404].includes(refreshError.response?.status)) unauthorized(original._authSession);
        throw refreshError;
      });
    };
    // Without an origin-wide lock, rotating refresh tokens can race. Preserve
    // credentials and fail explicitly rather than risking an unsafe replay.
    const flight = (navigator.locks
      ? navigator.locks.request('roam-auth-refresh', refresh)
      : Promise.reject(new Error('Automatic session refresh is unavailable in this browser. Please sign in again.')))
      .finally(() => {
        if (refreshFlight === flight) refreshFlight = undefined;
      });
    refreshFlight = flight;
  }
  await refreshFlight;
  if (!isCurrent()) throw changedAuth();
  return api(original);
});

// Runs after auth refresh, so a recovered 401 is not reported as a failure.
api.interceptors.response.use(response => response, reportApiRejection);
axios.interceptors.response.use(response => response, error => error.config?.skipSentryReporting ? Promise.reject(error) : reportApiRejection(error));

export default api;
